import { SyntheticEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Rights, User, useUsersQuery, Roles } from '../../graphql/types';
import { debounce, delay } from 'lodash';
import { Autocomplete, ClickAwayListener, Stack, TextField, Paper, Button } from '@mui/material';
import { PlusCircleOutlined } from '@ant-design/icons';
import { useFormikContext } from 'formik';
import ProfileAvatar from '../profiles/ProfileAvatar';
import UserListOption from './UserListOption';
import UserListButtonOption from './UserListButtonOption';
import { getUserProfileDropdown } from '../../utils/users';
import AddUser from './AddUser';
import useAuth from '../../hooks/useAuth';

export interface Props {
  id: string;
  disabled: boolean;
}

interface BrokerFormValues {
  broker: Partial<User> | null;
  manager: Partial<User> | null;
}

export const BrokerAutocomplete = ({ id, disabled }: Props) => {
  const { user: currentUser } = useAuth();
  const canCreate = currentUser?.rights.includes(Rights.CreateBrokerUser);
  const { errors, touched, values, handleBlur, setFieldValue } = useFormikContext<BrokerFormValues>();
  const [showInfo, setShowInfo] = useState<boolean>(disabled || !!values?.broker?.id);
  useEffect(() => {
    setShowInfo(disabled || !!values?.broker?.id);
  }, [disabled, values?.broker?.id]);
  const inputReference = useRef<HTMLInputElement>(null);
  const [users, setUsers] = useState<readonly User[]>([]);
  const [userInputValue, setUserInputValue] = useState<string>('');
  const {
    loading: usersLoading,
    data: usersData,
    refetch: usersRefetch
  } = useUsersQuery({
    variables: { right: Rights.IsBroker, isActive: true },
    fetchPolicy: 'network-only'
  });
  const usersFetch = useMemo(
    () =>
      debounce(
        (request: { input: string }) =>
          usersRefetch({ search: request.input }).then((rq) => setUsers((rq.data.users.data as User[]) || [])),
        200
      ),
    [usersRefetch]
  );
  useEffect(() => setUsers((usersData?.users.data as User[]) || []), [usersData?.users.data, usersLoading]);
  useEffect(() => {
    usersFetch({ input: userInputValue });
  }, [userInputValue, usersFetch]);

  const [isModalShown, showModal] = useState<boolean>(false);
  const handleAdd = () => {
    showModal(!isModalShown);
  };

  const handleSave = (broker: User) => {
    setFieldValue('broker', broker);
    setShowInfo(!!broker?.id);
    if (broker?.manager) {
      setFieldValue('manager', broker.manager);
    }
    showModal(!isModalShown);
  };

  const PaperComponentCustom = (options: any) => {
    const { containerProps, children } = options;
    return (
      <Paper {...containerProps}>
        <Stack>
          {children}
          {/* Verify permissions to add user */}
          {canCreate && (
            <Button
              variant="outlined"
              color="primary"
              startIcon={<PlusCircleOutlined />}
              onMouseDown={(event) => {
                event.preventDefault();
                handleAdd();
              }}
            >
              Add new Broker user
            </Button>
          )}
        </Stack>
      </Paper>
    );
  };

  return (
    <>
      <AddUser
        open={isModalShown}
        entity={null}
        title={'Broker'}
        role={[Roles.Broker]}
        onClose={handleAdd}
        handleAdd={handleAdd}
        onSave={handleSave}
      />
      {values?.broker?.id && showInfo && (
        <Stack direction={'row'}>
          <UserListButtonOption
            user={values.broker as User}
            type="Broker Name"
            showEditAction={!disabled}
            onClick={() => {
              if (disabled) return;
              setShowInfo(false);
              delay(() => inputReference.current?.focus(), 500);
            }}
          />
        </Stack>
      )}
      {!showInfo && (
        <ClickAwayListener
          onClickAway={() => {
            setShowInfo(disabled ? false : !!values?.broker?.id);
          }}
        >
          <Autocomplete
            PaperComponent={PaperComponentCustom}
            disabled={disabled}
            id={id}
            getOptionLabel={(option: Partial<User>) => getUserProfileDropdown(option as User)}
            options={users}
            autoComplete
            value={(values.broker?.id ? values.broker as User : null)}
            isOptionEqualToValue={(option: User, value: User) =>
              !!value?.id && option.id === value.id
            }
            onChange={(event: SyntheticEvent, newValue: User | null) => {
              setFieldValue('broker', newValue || null);
              setShowInfo(!!newValue?.id);
              setFieldValue('manager', newValue?.manager || { id: '' });
            }}
            onInputChange={(event: SyntheticEvent, newInputValue: string) => {
              setUserInputValue(newInputValue);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                placeholder={'Select Broker'}
                inputRef={inputReference}
                name="broker"
                onBlur={handleBlur}
                error={Boolean(touched.broker && errors.broker?.id)}
                helperText={touched.broker && errors.broker?.id}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: values?.broker?.id && <ProfileAvatar user={values?.broker as User} size={'sm'} />
                }}
                sx={{
                  '& fieldset': { border: 'none' },
                  '& .MuiInputLabel-root': { padding: '14px 0 8px 16px' },
                  '& .MuiOutlinedInput-root': { padding: '14px 0 8px 16px' }
                }}
              />
            )}
            renderOption={(props, option) => {
              const { key, ...rest } = props;
              return <UserListOption key={key} user={option as User} {...rest} />;
            }}
          />
        </ClickAwayListener>
      )}
    </>
  );
};
